import type {
  Applicant,
  ApplicationDetail,
  ApplicationDetailsCollateralPropertyResponse,
  ApplicationDetailsResponse,
  ApplicationListItem,
  CollateralProperty,
  HousingAssociation,
  LoanPart,
  MortgageDeed,
  PayoutDetails,
  PropertySale,
  PropertyValuation,
} from './application.types'
import { isInterestTerm } from '@/types/interest.types'

const toNumber = (value: string | number | null | undefined): number | undefined => {
  if (value == null) return undefined

  const numericValue = Number(value)
  return Number.isFinite(numericValue) ? numericValue : undefined
}

const toPercentage = (value: number | null): number | undefined => {
  if (value == null) return undefined
  return value * 100
}

const mapValuation = (
  valuation: ApplicationDetailsCollateralPropertyResponse['valuations']['currentValuation'],
  applicationId: string,
): PropertyValuation | undefined => {
  if (!valuation) return undefined

  return {
    source: valuation.type,
    createdAt: valuation.valuationDate,
    applicationId,
    value: toNumber(valuation.value) ?? 0,
  }
}

const mapMortgageDeed = (
  mortgageDeed: ApplicationDetailsCollateralPropertyResponse['mortgageDeeds'][number],
): MortgageDeed => ({
  use: mortgageDeed.use ?? false,
  ours: mortgageDeed.ours ?? false,
  ...(mortgageDeed.index != null ? { index: mortgageDeed.index } : {}),
  amount: mortgageDeed.amount,
  exists: mortgageDeed.exists ?? false,
  ...(mortgageDeed.number ? { number: mortgageDeed.number } : {}),
  isPaper: mortgageDeed.isPaper ?? false,
  ...(mortgageDeed.createdAt ? { createdAt: mortgageDeed.createdAt } : {}),
  ...(mortgageDeed.propertyLabel ? { propertyLabel: mortgageDeed.propertyLabel } : {}),
})

const mapHousingAssociation = (
  housingAssociation: ApplicationDetailsCollateralPropertyResponse['housingAssociation'],
): HousingAssociation | undefined => {
  if (!housingAssociation) return undefined

  return {
    id: '',
    name: housingAssociation.name ?? '',
    organisationNumber: housingAssociation.organisationNumber ?? '',
    address: {
      street: housingAssociation.address.street ?? '',
      city: housingAssociation.address.city ?? '',
      zip: toNumber(housingAssociation.address.zip) ?? 0,
    },
    ...(housingAssociation.administrator
      ? {
          administrator: {
            name: housingAssociation.administrator.name ?? '',
          },
        }
      : {}),
  }
}

const mapCollateralProperty = (
  collateralProperty: ApplicationDetailsResponse['collateralProperty'],
  applicationId: string,
): CollateralProperty | undefined => {
  if (!collateralProperty) return undefined

  const collateralDetails = {
    ...(toNumber(collateralProperty.size) != null ? { size: toNumber(collateralProperty.size) } : {}),
    ...(toNumber(collateralProperty.rooms) != null ? { rooms: toNumber(collateralProperty.rooms) } : {}),
    ...(toNumber(collateralProperty.floors) != null ? { floors: toNumber(collateralProperty.floors) } : {}),
    ...(toNumber(collateralProperty.nbrOfFloors) != null
      ? { nbrOfFloors: toNumber(collateralProperty.nbrOfFloors) }
      : {}),
    ...(collateralProperty.balcony != null ? { balcony: collateralProperty.balcony } : {}),
    ...(collateralProperty.elevator != null ? { elevator: collateralProperty.elevator } : {}),
  }
  const base = {
    id: applicationId,
    monthlyFee: collateralProperty.monthlyCost ?? 0,
    street: collateralProperty.address.street,
    ...(collateralProperty.address.streetNo ? { streetNo: collateralProperty.address.streetNo } : {}),
    hasStreetNo: Boolean(collateralProperty.address.streetNo),
    kommun: collateralProperty.address.municipality,
    city: collateralProperty.address.city,
    zip: collateralProperty.address.zip ?? 0,
    ...(toNumber(collateralProperty.taxValue) != null ? { taxValue: toNumber(collateralProperty.taxValue) } : {}),
    valuation: mapValuation(collateralProperty.valuations.currentValuation, applicationId),
    ...(collateralProperty.insurance
      ? {
          insurance: {
            isInsured: collateralProperty.insurance.isInsured ?? false,
            ...(collateralProperty.insurance.insuranceCompany
              ? { insuranceCompany: collateralProperty.insurance.insuranceCompany }
              : {}),
            ...(collateralProperty.insurance.insuranceCompanyOtherName
              ? { insuranceCompanyOtherName: collateralProperty.insurance.insuranceCompanyOtherName }
              : {}),
            ...(collateralProperty.insurance.isFullyInsured != null
              ? { isFullyInsured: collateralProperty.insurance.isFullyInsured }
              : {}),
            ...(collateralProperty.insurance.coverageAmount != null
              ? { coverageAmount: collateralProperty.insurance.coverageAmount }
              : {}),
            ...(collateralProperty.insurance.excessAmount != null
              ? { excessAmount: collateralProperty.insurance.excessAmount }
              : {}),
          },
        }
      : {}),
  }

  if (collateralProperty.type === 'apartment') {
    const apartmentNumber = collateralProperty.address.apartmentNumber

    return {
      ...base,
      type: 'apartment',
      ...(collateralProperty.housingAssociation?.housingAssociationApartmentNumber
        ? { apartmentNbrBRF: collateralProperty.housingAssociation.housingAssociationApartmentNumber }
        : {}),
      ...(collateralProperty.housingAssociation?.name
        ? { housingAssociationName: collateralProperty.housingAssociation.name }
        : {}),
      housingAssociation: mapHousingAssociation(collateralProperty.housingAssociation),
      ...collateralDetails,
      ...(apartmentNumber
        ? { hasApartmentNbr: true as const, apartmentNbr: apartmentNumber }
        : { hasApartmentNbr: false as const }),
    }
  }

  if (collateralProperty.type === 'house' || collateralProperty.type === 'vacation') {
    return {
      ...base,
      type: collateralProperty.type,
      size: toNumber(collateralProperty.size) ?? 0,
      ...(collateralProperty.propertyLabel ? { propertyLabel: collateralProperty.propertyLabel } : {}),
      ...(collateralProperty.titleDeed ? { titleDeed: collateralProperty.titleDeed } : {}),
      mortgageDeeds: collateralProperty.mortgageDeeds.map(mapMortgageDeed),
    }
  }

  return undefined
}

const mapApplicant = (applicant: ApplicationDetailsResponse['applicants'][number]): Applicant => ({
  id: applicant.userId,
  userId: applicant.userId,
  personalNumber: toNumber(applicant.personalNumber) ?? 0,
  givenName: applicant.givenName,
  surname: applicant.surname,
  monthlyIncome: applicant.monthlyIncome ?? undefined,
  maritalStatus: applicant.maritalStatus ?? undefined,
  employmentInfo: applicant.employmentInfo,
  hasOpenKyc: applicant.hasOpenKyc,
  roles: applicant.roles,
  email: applicant.contactDetails.email ?? undefined,
  phoneNumber: applicant.contactDetails.phoneNumber ?? undefined,
  contactDetails: {
    phone: applicant.contactDetails.phoneNumber ?? undefined,
    email: applicant.contactDetails.email ?? undefined,
  },
})

const mapLoanPart = (loan: ApplicationDetailsResponse['loansBreakdown'][number]): LoanPart => ({
  amount: loan.amount,
  amortization: loan.amortization ?? 0,
  interestTerm: isInterestTerm(loan.interestTerm) ? loan.interestTerm : 'threeMonth',
  ...(loan.interestRate != null ? { interestRate: loan.interestRate } : {}),
  ...(loan.effectiveInterestRate != null ? { effectiveInterestRate: loan.effectiveInterestRate } : {}),
  ...(loan.code ? { code: loan.code } : {}),
})

const mapPropertySale = (
  propertySale: ApplicationDetailsResponse['unsecuredLoansBreakdown']['propertySales'][number],
): PropertySale => ({
  purchasePrice: propertySale.purchasePrice ?? undefined,
  salePrice: propertySale.salePrice ?? undefined,
  saleLoans: propertySale.saleLoans ?? undefined,
  saleFee: propertySale.saleFee ?? undefined,
  deferredTax: propertySale.deferredTax ?? undefined,
  taxNow: propertySale.taxNow ?? undefined,
  saleCompleteDate: propertySale.saleCompleteDate,
  excessFromSales: propertySale.excessFromSales,
})

const mapPayoutDetails = (response: ApplicationDetailsResponse): PayoutDetails => ({
  payoutDate: response.plannedPayoutDetails.payoutDate,
  ...(response.plannedPayoutDetails.payoutDateASAP != null
    ? { payoutDateASAP: response.plannedPayoutDetails.payoutDateASAP }
    : {}),
  finalPayoutDate: response.finalPayoutDate,
  plannedPayoutDetails: response.plannedPayoutDetails.details,
})

export const mapApplicationDetailsResponseToApplicationListItem = (
  response: ApplicationDetailsResponse,
): ApplicationListItem => ({
  id: response.id,
  brokerExternalId: response.brokerExternalId,
  debtId: response.debtId,
  status: response.status,
  type: response.type,
  amountAppliedFor: toNumber(response.amountAppliedFor),
  createdAt: response.createdAt,
  updatedAt: response.updatedAt,
  submittedAt: response.submittedAt,
  openApplicationAdditionRequests: response.openApplicationAdditionRequests,
  applicants: response.applicants.map((applicant) => ({
    id: applicant.userId,
    name: `${applicant.givenName} ${applicant.surname}`.trim(),
    personalNumber: toNumber(applicant.personalNumber),
    isMainApplicant: applicant.roles.isMainApplicant,
  })),
})

export const mapApplicationDetailsResponseToApplicationDetail = (
  response: ApplicationDetailsResponse,
): ApplicationDetail => {
  const applicants = response.applicants.map(mapApplicant)

  return {
    id: response.id,
    status: response.status,
    type: response.type,
    brokerExternalId: response.brokerExternalId,
    debtId: response.debtId,
    createdAt: response.createdAt,
    updatedAt: response.updatedAt,
    submittedAt: response.submittedAt,
    amountAppliedFor: toNumber(response.amountAppliedFor),
    openApplicationAdditionRequests: response.openApplicationAdditionRequests,
    applicants,
    caseId: response.caseId ?? undefined,
    comment: response.comment ?? undefined,
    household: response.household ?? undefined,
    topupPurpose: response.topupPurpose ?? undefined,
    topupPurposeOther: response.topupPurposeOther ?? undefined,
    isHighPrio: response.isHighPrio ?? undefined,
    isNewProduction: response.isNewProduction ?? undefined,
    propertyEstimation: toNumber(response.propertyValueEstimation),
    requestedFeatures: response.requestedFeatures ?? undefined,
    amountApproved: response.amountApproved ?? undefined,
    minAmortization: response.minAmortization ?? undefined,
    collateralProperty: mapCollateralProperty(response.collateralProperty, response.id),
    unsecuredLoanPackage: {
      loans: [],
      propertySales: response.unsecuredLoansBreakdown.propertySales.map(mapPropertySale),
    },
    ltv: {
      appliedFor: toPercentage(response.ltv.appliedFor) ?? 0,
      ...(response.ltv.approved != null ? { approved: toPercentage(response.ltv.approved) } : {}),
    },
    parties: response.parties,
    payoutDetails: mapPayoutDetails(response),
    ...(response.downPaymentSource
      ? {
          downPayment: response.downPaymentSource.downPayment,
          cashContributionMoneySources: response.downPaymentSource.cashContribution,
        }
      : {}),
    loans: response.loansBreakdown.map(mapLoanPart),
    loanAgreements: response.loanAgreements,
    depositLoanAgreements: response.depositLoanAgreements,
    energyDeclaration: response.collateralProperty?.energyDeclaration
      ? {
          energyClass: response.collateralProperty.energyDeclaration.energyClass,
        }
      : undefined,
  }
}
